// src/components/kioskPhotos/PhotoCropper.jsx
//
// Crops a photo to the kiosk screen: an 800x480 frame (shown smaller),
// drag to move, slider to zoom. "Enregistrer" draws the frame on a canvas
// and encodes a baseline JPEG (what browsers' canvas produce, and what the
// kiosk's decoder needs), lowering the quality until it fits in 100 KB.
// The home screen's card can be shown on top to check the text stays readable.
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Slider, Stack, TextField,
  Typography, FormControlLabel, Switch, Box, Alert,
} from '@mui/material';
import { FRAME_W, FRAME_H, coverScale, clampOffset, centredOffset, rescaleOffset } from './cropMath';

const VIEW_W = 560;                      // the frame on screen
const VIEW_SCALE = VIEW_W / FRAME_W;
const MAX_BYTES = 100 * 1024; // what the kiosk can keep (backend routes/kioskPhotos.ts)

/** The 800x480 crop as a JPEG blob, under MAX_BYTES if possible. */
async function encode(img, offset, scale) {
  const canvas = document.createElement('canvas');
  canvas.width = FRAME_W;
  canvas.height = FRAME_H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, FRAME_W, FRAME_H);
  ctx.drawImage(img, offset.x, offset.y, img.naturalWidth * scale, img.naturalHeight * scale);
  for (const q of [0.85, 0.78, 0.7, 0.62, 0.55, 0.48, 0.42]) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    if (blob && blob.size <= MAX_BYTES) return blob;
  }
  return null;
}

export default function PhotoCropper({ file, onCancel, onDone }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [name, setName] = useState('');
  const [overlay, setOverlay] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const drag = useRef(null);

  useEffect(() => {
    if (!file) return undefined;
    setName(file.name.replace(/\.[^.]+$/, '').slice(0, 120));
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const s = coverScale(image.naturalWidth, image.naturalHeight);
      setImg(image);
      setZoom(1);
      setOffset(centredOffset(image.naturalWidth, image.naturalHeight, s));
    };
    image.onerror = () => setError('Image illisible.');
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const base = img ? coverScale(img.naturalWidth, img.naturalHeight) : 1;
  const scale = base * zoom;
  const lowRes = img && base > 1.05; // the photo is smaller than the screen: it will look soft

  const onZoom = (_e, z) => {
    if (!img) return;
    setOffset((o) => rescaleOffset(o, base * zoom, base * z, img.naturalWidth, img.naturalHeight));
    setZoom(z);
  };
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, start: offset };
  };
  const onPointerMove = (e) => {
    if (!drag.current || !img) return;
    const dx = (e.clientX - drag.current.x) / VIEW_SCALE;
    const dy = (e.clientY - drag.current.y) / VIEW_SCALE;
    setOffset(clampOffset(drag.current.start.x + dx, drag.current.start.y + dy, img.naturalWidth, img.naturalHeight, scale));
  };
  const onPointerUp = () => { drag.current = null; };

  const save = async () => {
    setBusy(true); setError('');
    try {
      const blob = await encode(img, offset, scale);
      if (!blob) throw new Error('Photo trop détaillée pour tenir en 100 Ko : zoomez un peu ou choisissez-en une autre.');
      await onDone(blob, name.trim() || 'Photo');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={Boolean(file)} onClose={busy ? undefined : onCancel} maxWidth="md">
      <DialogTitle>Cadrer la photo pour l’écran de la borne</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {lowRes && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Photo plus petite que l’écran ({img.naturalWidth}×{img.naturalHeight}) : elle sera agrandie et un peu floue.
            Idéalement au moins 800×480, en paysage.
          </Alert>
        )}
        <Box
          role="presentation"
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
          sx={{ width: VIEW_W, height: FRAME_H * VIEW_SCALE, position: 'relative', overflow: 'hidden', bgcolor: '#000',
            borderRadius: 1, cursor: 'grab', touchAction: 'none', userSelect: 'none' }}>
          {img && (
            <img src={img.src} alt="" draggable={false} style={{
              position: 'absolute', left: offset.x * VIEW_SCALE, top: offset.y * VIEW_SCALE,
              width: img.naturalWidth * scale * VIEW_SCALE, height: img.naturalHeight * scale * VIEW_SCALE, maxWidth: 'none',
            }} />
          )}
          {overlay && (
            // The home screen's card (design G), scaled like the frame.
            <Box sx={{ position: 'absolute', left: 28 * VIEW_SCALE, top: 28 * VIEW_SCALE, bottom: 28 * VIEW_SCALE,
              width: 430 * VIEW_SCALE, borderRadius: `${22 * VIEW_SCALE}px`, bgcolor: '#141b36', color: '#fff',
              p: `${20 * VIEW_SCALE}px`, boxSizing: 'border-box', pointerEvents: 'none' }}>
              <Typography sx={{ fontSize: 42 * VIEW_SCALE, fontWeight: 800, lineHeight: 1.15 }}>Un mot sur votre soirée ?</Typography>
              <Box sx={{ position: 'absolute', left: 30 * VIEW_SCALE, right: 30 * VIEW_SCALE, bottom: 40 * VIEW_SCALE,
                height: 84 * VIEW_SCALE, borderRadius: 99, bgcolor: '#f5b301' }} />
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Zoom</Typography>
          <Slider value={zoom} min={1} max={3} step={0.01} onChange={onZoom} aria-label="Zoom" disabled={!img} />
          <FormControlLabel control={<Switch checked={overlay} onChange={(e) => setOverlay(e.target.checked)} />}
            label="Aperçu du texte" sx={{ whiteSpace: 'nowrap' }} />
        </Stack>
        <Typography variant="caption" color="text.secondary">Glissez la photo pour la cadrer. La partie gauche est couverte par le texte.</Typography>
        <TextField fullWidth size="small" label="Nom" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 2 }}
          inputProps={{ maxLength: 120 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={busy || !img}>{busy ? 'Envoi…' : 'Enregistrer'}</Button>
      </DialogActions>
    </Dialog>
  );
}
